import React from 'react';
import Link from 'next/link';
import {
  ExternalQuotationItem,
  PendingExternalQuotationItem, QuotationItemStatuses
} from "../../types/models/quotation_item";
import useMessagingSocket from "../../hooks/use_messaging_socket";
import {useDispatch} from "react-redux";
import {useQuery} from "../../hooks/use_query";
import {actions} from "../../store/actions";
import * as gtag from "../../lib/gtag";
import {CallbackFunctionVariadic} from "../../types/function";
import {GroupInfo} from "../../types/api/messaging/group";
import {findIndex, orderBy} from "lodash";
import Api from "../../service/api";
import ChatWindow from "../chat_window";
import styles from "../../styles/external_quotations_page.module.scss";
import LayoutViewport from "../layout/layout_viewport";
import HeadTitle from "../head_title";
import classNames from "classnames";
import TimeoutAlert from "../timeout_alert";
import {GoChevronLeft} from "react-icons/go";
import {genericTextChange} from "../../utils/forms";
import RequestExternalQuotationModal from "./request_modal";
import {getExternalQuotationItemStatusDisplayMessage} from "../../lib/quotation_item";
import ViewExternalQuotationModal from "./view_modal";
import {useRouter} from "next/router";

function getChatGroupRef(quotationRef: string) {
  return `external_quotation_${quotationRef}`;
}

export default function ExternalQuotationListing() {
  const [requestDialogShown, setRequestDialogShown] = React.useState(false);
  const [externalQuotations, setExternalQuotations] = React.useState<ExternalQuotationItem[]>([]);
  const [searchText, setSearchText] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [selectedQuotationRefNo, setSelectedQuotationRefNo] = React.useState<string | null>(null);
  const [unseenMessageCount, setUnseenMessageCount] = React.useState<Record<string, number>>({});
  const [requestingLink, setRequestingLink] = React.useState<string | undefined>(undefined);
  const [showLinkWarning, setShowLinkWarning] = React.useState<boolean>(false);
  const [showQuotationModal, setShowQuotationModal] = React.useState(false);

  const messagingErrorCallback = React.useCallback((message: string) => setErrorMessage(message), []);
  const groupsInfoFetchedRef = React.useRef(false);

  const router = useRouter();
  const messagingSocket = useMessagingSocket(messagingErrorCallback);
  const dispatch = useDispatch();
  const requestingLinkRaw = useQuery('requestLink');

  const socket = React.useMemo(() => {
    return messagingSocket;
  }, [messagingSocket])

  React.useEffect(() => {
    if (!requestingLinkRaw) {
      return;
    }

    try {
      new URL(requestingLinkRaw);
    } catch (e) {
      return;
    }

    setShowLinkWarning(window.location.href.length >= 2048);
    setRequestingLink(requestingLinkRaw);
    setRequestDialogShown(true);
  }, [requestingLinkRaw]);

  const showRequestDialog = React.useCallback(() => setRequestDialogShown(true), []);
  const hideRequestDialog = React.useCallback(() => setRequestDialogShown(false), []);

  const onExternalQuotationAdded = React.useCallback((quotationItem: PendingExternalQuotationItem) => {

    // add the quotation item into cart
    dispatch(actions.quotationItem.addItem(quotationItem));

    gtag.addToCart({
      type: 'quotation_external',
      isQuotation: true,
      item: quotationItem,
      product: undefined
    });

    // show the quotation item as a chat item
    setExternalQuotations([quotationItem, ...externalQuotations]);
    if (socket) {
      socket.emit('updateRooms');
    }

    // close the dialog
    setRequestDialogShown(false);
    // remove search from url
    router.replace(window.location.pathname, undefined, {shallow: true});
  }, [externalQuotations, socket, router]);

  React.useEffect(() => {
    if (!socket || externalQuotations.length === 0 || groupsInfoFetchedRef.current) {
      return;
    }

    let listenerRemoved = false;
    let groupsInfoListener: CallbackFunctionVariadic;
    socket.once('groupsInfo', groupsInfoListener = (groupsInfo: Record<string, GroupInfo>) => {
      const quotationChatGroupRefs = new Set(externalQuotations.map(item => getChatGroupRef(item.referenceNumber)));
      const groupRefTimingPairs = Object.entries(groupsInfo)
        .filter(([chatGroupRef]) => {
          return quotationChatGroupRefs.has(chatGroupRef);
        });
      const newUnseenMessageCount = groupRefTimingPairs.reduce((agg, [chatGroupRef, groupInfo]) => {
        agg[chatGroupRef] = groupInfo.unseenMessagesCount;
        return agg;
      }, {} as Record<string, number>);
      setUnseenMessageCount(newUnseenMessageCount);

      const sortedQuotationItems = orderBy(externalQuotations, [(quotation) => {
        const groupRef = getChatGroupRef(quotation.referenceNumber);
        const groupInfo = groupsInfo[groupRef];

        const times: number[] = [];
        if (groupInfo) {
          times.push(groupInfo.lastMessageTime || 0);
        }

        times.push(quotation.createdAt);
        return Math.max(...times);
      }], ['desc']);

      if (!listenerRemoved) {
        setExternalQuotations(sortedQuotationItems);
        groupsInfoFetchedRef.current = true;
      }
    });

    socket.emit('getGroupsInfo');

    return () => {
      listenerRemoved = true;
      socket.off('groupsInfo', groupsInfoListener);
    };
  }, [socket, externalQuotations]);

  React.useEffect(() => {
    if (!socket) {
      return;
    }

    let onNewMessageListener: CallbackFunctionVariadic;
    let onMessagesSeenListener: CallbackFunctionVariadic;

    socket.once('seenMessages', onMessagesSeenListener = (groupRef: string, lastSeenTiming: number, groupUnseenMessageCount: number) => {
      if (!groupRef.startsWith('external_quotation_')) {
        return;
      }

      setUnseenMessageCount({
        ...unseenMessageCount,
        [groupRef]: groupUnseenMessageCount
      });
    });

    socket.once('newMessage', onNewMessageListener = (groupRef: string) => {
      if (!groupRef.startsWith('external_quotation_')) {
        return;
      }
      const quotationReferenceNumber = groupRef.substring('external_quotation_'.length);
      const quotationItemIndex = findIndex(externalQuotations, {
        referenceNumber: quotationReferenceNumber
      });
      const quotationItem = externalQuotations[quotationItemIndex];

      const externalQuotationsClone = [quotationItem, ...externalQuotations];
      externalQuotationsClone.splice(quotationItemIndex + 1, 1);
      setExternalQuotations(externalQuotationsClone);

      setUnseenMessageCount({
        ...unseenMessageCount,
        [groupRef]: (unseenMessageCount[groupRef] ?? 0) + 1
      });
    });

    return () => {
      socket.off('seenMessages', onMessagesSeenListener);
      socket.off('newMessage', onNewMessageListener);
    };
  }, [socket, unseenMessageCount, externalQuotations]);

  React.useEffect(() => {
    (async () => {
      try {
        const externalQuotationsRes = await Api.quotationItem.getAllExternal();
        const {quotationItems} = externalQuotationsRes.data;

        setExternalQuotations(quotationItems);
      } catch (e) {
        setErrorMessage('Failed to fetch external quotations');
      }
    })();
  }, []);

  const getChatWindow = React.useCallback((quotationReferenceNumber: string) => {
    const chatGroupRef = getChatGroupRef(quotationReferenceNumber);

    return <ChatWindow
      key={quotationReferenceNumber}
      groupRef={chatGroupRef}
      onErrorMessage={setErrorMessage}
      socket={socket}
      className={styles.chatWindow}
    />
  }, [externalQuotations, unseenMessageCount, socket]);

  const selectedQuotation = React.useMemo(() => {
    return externalQuotations.find(quotation => quotation.referenceNumber === selectedQuotationRefNo);
  }, [selectedQuotationRefNo, externalQuotations]);

  const handleViewQuotation = React.useCallback(() => setShowQuotationModal(true), []);
  const hideQuotationModal = React.useCallback(() => setShowQuotationModal(false), []);

  const handleBackFromChat = React.useCallback(() => {
    setSelectedQuotationRefNo(null);
  }, []);

  const itemChangeHandler = React.useCallback((deleted: boolean, updatedItem?: ExternalQuotationItem) => {
    if (!selectedQuotation) {
      return;
    }

    const index = externalQuotations.indexOf(selectedQuotation);
    const quotationsCloned = externalQuotations.slice();
    if (deleted) {
      quotationsCloned.splice(index, 1);
      setExternalQuotations(quotationsCloned);
      return;
    }

    if (updatedItem) {
      quotationsCloned[index] = updatedItem;
      setExternalQuotations(quotationsCloned);
    }
  }, [externalQuotations, selectedQuotation]);

  const viewingChat = !!selectedQuotationRefNo;
  let displayStatusMessage: string = '';
  if (viewingChat) {
    const selectedQuotation = externalQuotations.find(quotation => quotation.referenceNumber === selectedQuotationRefNo);
    if (selectedQuotation) {
      displayStatusMessage = getExternalQuotationItemStatusDisplayMessage(selectedQuotation);
    }
  }

  return (
    <LayoutViewport contentClassName={styles.layoutContent}>
      <HeadTitle title="External Quotations" />

      <div className={classNames('container-lg', 'h-100', {
        [styles.viewingChat]: viewingChat
      })}>
        <TimeoutAlert
          message={errorMessage}
          onHide={() => setErrorMessage(null)}
        />

        <div className={classNames(styles.pageNav, 'd-flex', 'flex-row')}>
          <div className="col flex-grow-1 flex-shrink-1">
            <button className={classNames(styles.closeChat, 'btn', 'btn-link')} onClick={handleBackFromChat}>
              <GoChevronLeft />
              Back
            </button>
            <h2 className={styles.pageTitle}>QUOTATIONS</h2>
          </div>
          <div className="flex-grow-0 flex-shrink-0">
            <button
              className={classNames('btn', 'btn-primary', styles.requestButton)}
              onClick={showRequestDialog}
            >
              Request for Quotation
            </button>
          </div>
        </div>

        {externalQuotations.length > 0 ? (
          <div className={classNames('row', styles.chatBox)}>
            <div className={classNames('col-lg-4', styles.sidebar)}>
              <div className="bg-muted py-2 px-1 mb-2">
                <input
                  type="text"
                  placeholder="Search"
                  className="form-control"
                  value={searchText}
                  onChange={genericTextChange(setSearchText)}
                />
              </div>
              <div className={classNames('list-group', styles.quotationItemChatItems)}>
                {externalQuotations
                  .filter(quotationItem => {
                    return searchText.length === 0 ||
                      quotationItem.referenceNumber.includes(searchText) ||
                      quotationItem.instructions.includes(searchText) ||
                      (quotationItem.productSourceData.link || '').includes(searchText)
                  })
                  .map((quotationItem) => {
                    const isItemActive = selectedQuotationRefNo === quotationItem.referenceNumber;
                    const chatGroupRef = getChatGroupRef(quotationItem.referenceNumber);
                    return (
                      <div
                        className={classNames('list-group-item', styles.quotationItemChatItem, {
                          active: isItemActive,
                          [styles.quotationItemChatItemActive]: isItemActive
                        })}
                        key={quotationItem.referenceNumber}
                      >
                        <div
                          className="media"
                          onClick={() => setSelectedQuotationRefNo(quotationItem.referenceNumber)}
                          title={quotationItem.productSourceData.link}
                        >
                          <div className={`align-self-center mr-2 ${styles.quotationItemImageContainer}`}>
                            <img src={quotationItem.mainImage} alt="quotation item image" />
                          </div>

                          <div className="media-body align-self-center">
                            <div className="font-weight-semibold">{quotationItem.name}</div>
                            <div className={styles.quotationItemChatItemSubtitle}>
                              QUOTE ID #{quotationItem.referenceNumber}
                            </div>
                          </div>

                          {!!unseenMessageCount[chatGroupRef] && (
                            <div className={classNames(
                              styles.quotationItemChatItemUnreadCount,
                              'align-self-center',
                              'font-weight-bold',
                              {
                                'bg-primary': !isItemActive,
                                'bg-warning': isItemActive,
                                'text-dark': isItemActive,
                                'text-light': !isItemActive
                              }
                            )}>
                              {unseenMessageCount[chatGroupRef]}
                            </div>
                          )}

                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>
            <div className={classNames('col-lg-8', styles.chatWindowContainer)}>
              {selectedQuotation && (
                <>
                  <div className={classNames(styles.pinnedMessage, 'animate__animated', 'animate__bounceIn')}>
                    <div className={styles.status}>
                      {displayStatusMessage}
                    </div>
                    <div className={styles.buttons}>
                      <button
                        className="btn btn-sm btn-warning mr-1"
                        onClick={handleViewQuotation}
                      >
                        View
                      </button>
                      {selectedQuotation.status === QuotationItemStatuses.resolved && (
                        <Link
                          href={selectedQuotation ? `/cart?itemId=${selectedQuotation._id}` : '/cart'}
                          className="btn btn-sm btn-primary">
                          Checkout
                        </Link>
                      )}
                    </div>
                  </div>

                  {getChatWindow(selectedQuotation.referenceNumber)}
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center">No quotations yet.</div>
        )}

      </div>

      <RequestExternalQuotationModal
        onCancel={hideRequestDialog}
        onSuccess={onExternalQuotationAdded}
        show={requestDialogShown}
        showLinkWarning={showLinkWarning}
        requestLink={requestingLink}
      />

      <ViewExternalQuotationModal
        show={showQuotationModal}
        onCancel={hideQuotationModal}
        externalQuotationItem={selectedQuotation}
        onChange={itemChangeHandler}
      />
    </LayoutViewport>
  );

}